import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import sendEmailSupport from '@api/support';
import { FormError } from '@typings/common';
import { ApiResponse } from '@typings/ApiResponse';
import ErrorSummary from '../ErrorSummary';
import validateSupportFormField from './utils';

interface SupportedUser {
  name: string;
  email: string;
  description: string;
}

const SupportForm = () => {
  const [isShowFormErrors, setIsShowFormErrors] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<SupportedUser>({
    name: '',
    email: '',
    description: '',
  });

  const [supportFormErrors, setSupportFormErrors] = useState<FormError[]>([
    {
      id: 'name',
      message: '',
    },
    {
      id: 'email',
      message: '',
    },
    {
      id: 'description',
      message: '',
    },
  ]);
  const navigate = useNavigate();

  const handleInputChange = (
    field: 'name' | 'email' | 'description',
    value: string
  ) => {
    setUserInfo(prevState => ({ ...prevState, [field]: value }));
    const errorMessage = validateSupportFormField(field, value);
    const updatedErrors = supportFormErrors.map(err =>
      err.id === field ? { ...err, message: errorMessage } : err
    );

    setSupportFormErrors(updatedErrors);

    if (updatedErrors.some(err => err.message !== '')) {
      setIsShowFormErrors(true);
    } else {
      setIsShowFormErrors(false);
    }
  };

  const onFinish = async (e: any) => {
    e.preventDefault();

    const errors = supportFormErrors.map(err => ({
      ...err,
      message: validateSupportFormField(
        err.id as 'name' | 'email' | 'description',
        userInfo[err.id as keyof SupportedUser] || ''
      ),
    }));

    setSupportFormErrors(errors);

    // Check if any error messages exist
    if (errors.some(err => err.message !== '')) {
      setIsShowFormErrors(true);
      return;
    }

    setIsLoading(true);
    try {
      const response: ApiResponse<null> = await sendEmailSupport(userInfo);
      if (response && response.status === 'success') {
        setUserInfo({
          name: '',
          email: '',
          description: '',
        });
        localStorage.setItem('contact-success', 'show');
        navigate('/contact/confirmation');
      }
    } catch (error: any) {
      console.error(error);
      setSupportFormErrors([
        ...supportFormErrors,
        {
          id: 'api',
          message: error ? error?.message : 'Failed to require support',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <form>
        {isShowFormErrors && <ErrorSummary errors={supportFormErrors} />}

        <div
          className={`govuk-form-group ${supportFormErrors?.find(err => err.id === 'name')?.message ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="full-name-input">
            Your full name
          </label>
          <div className="govuk-hint" id="full-name-hint" />
          <input
            className={`govuk-input govuk-input--width-20 ${supportFormErrors?.find(err => err.id === 'name')?.message ? 'govuk-input--error' : ''}`}
            id="full-name-input"
            name="name"
            type="text"
            aria-describedby={
              supportFormErrors?.find(err => err.id === 'name')?.message
                ? 'full-name-input-error'
                : undefined
            }
            autoComplete="name"
            value={userInfo.name}
            onChange={e => handleInputChange('name', e.target.value)}
          />

          {supportFormErrors?.find(err => err.id === 'name')?.message && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {supportFormErrors?.find(err => err.id === 'name')?.message}
            </p>
          )}
        </div>

        <div
          className={`govuk-form-group ${supportFormErrors?.find(err => err.id === 'email')?.message ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="email">
            Email address associated with your MyWishes account
          </label>
          <input
            className={`govuk-input govuk-input--width-20 ${supportFormErrors?.find(err => err.id === 'email')?.message ? 'govuk-input--error' : ''}`}
            id="email"
            name="email"
            type="email"
            spellCheck={false}
            aria-describedby="email-hint"
            autoComplete="email"
            value={userInfo.email}
            onChange={e => handleInputChange('email', e.target.value)}
          />
          {supportFormErrors?.find(err => err.id === 'email')?.message && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {supportFormErrors?.find(err => err.id === 'email')?.message}
            </p>
          )}
        </div>

        <div
          className={`govuk-form-group ${supportFormErrors?.find(err => err.id === 'description')?.message ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="description-input">
            Add your comment or question in the box below
          </label>
          <textarea
            className={`govuk-textarea ${supportFormErrors?.find(err => err.id === 'description')?.message ? 'govuk-textarea--error' : ''}`}
            id="description-input"
            name="description"
            rows={5}
            autoComplete="description"
            aria-describedby={
              supportFormErrors?.find(err => err.id === 'description')?.message
                ? 'description-input-error'
                : undefined
            }
            value={userInfo.description}
            onChange={e => handleInputChange('description', e.target.value)}
          />

          {supportFormErrors?.find(err => err.id === 'description')
            ?.message && (
            <p id="full-name-input-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>
              {
                supportFormErrors?.find(err => err.id === 'description')
                  ?.message
              }
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="govuk-button"
          onClick={onFinish}
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default SupportForm;
